import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RepeatingInvoice } from '../../../types/accounting';

interface RepeatingInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  repeatingInvoice: RepeatingInvoice | null;
}

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return 'Not set';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'Not set';
  return date.toLocaleDateString('en-AU', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getScheduleDescription = (frequency: RepeatingInvoice['frequency']): string => {
  switch (frequency) {
    case 'WEEKLY':
      return 'Weekly';
    case 'MONTHLY':
      return 'Monthly';
    case 'QUARTERLY':
      return 'Quarterly';
    case 'YEARLY':
      return 'Yearly';
    default:
      return 'Monthly';
  }
};

const getStatusBadgeClass = (status: RepeatingInvoice['status']): string => {
  switch (status) {
    case 'ACTIVE':
      return 'bg-success';
    case 'PAUSED':
      // badge-light-warning instead of `bg-warning text-dark`: the dark themes remap
      // .text-dark to a light colour, leaving near-white text on amber.
      return 'badge-light-warning';
    case 'CANCELLED':
      return 'bg-danger';
    default:
      return 'bg-secondary';
  }
};

const getStatusLabel = (status: RepeatingInvoice['status']): string => {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'PAUSED':
      return 'Paused';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return status;
  }
};

const RepeatingInvoiceModal: React.FC<RepeatingInvoiceModalProps> = ({
  isOpen,
  onClose,
  repeatingInvoice,
}) => {
  // Hook must run before the early return, or the modal opening/closing changes hook order.
  const navigate = useNavigate();

  if (!isOpen || !repeatingInvoice) return null;

  const total = (repeatingInvoice.lineItems || []).reduce((sum, li) => sum + (li.lineAmount ?? 0), 0);
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);

  const isExpense = repeatingInvoice.templateType === 'bill';
  const typeLabel = isExpense ? 'Recurring Expense' : 'Recurring Invoice';
  const typeIcon = isExpense ? 'bi-credit-card' : 'bi-arrow-repeat';
  // Theme tokens rather than fixed 80%-alpha green/red, which the dark themes can't restate.
  const typeColor = isExpense ? 'var(--bs-danger)' : 'var(--bs-success)';
  const typeBgSubtle = isExpense ? 'var(--bs-danger-bg-subtle)' : 'var(--bs-success-bg-subtle)';
  const typeBorderSubtle = isExpense ? 'var(--bs-danger-border-subtle)' : 'var(--bs-success-border-subtle)';
  const typeTextEmphasis = isExpense ? 'var(--bs-danger-text-emphasis)' : 'var(--bs-success-text-emphasis)';
  const amountPrefix = isExpense ? '-' : '+';

  return (
    <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          <div className="modal-header border-bottom">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: '40px',
                  height: '40px',
                  background: typeColor,
                  borderRadius: '10px',
                  color: 'white',
                }}
              >
                <i className={`bi ${typeIcon} fs-5`}></i>
              </div>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="modal-title mb-0 fw-bold">
                    {repeatingInvoice.contactName || 'Unknown'}
                  </h5>
                  <span
                    className="badge"
                    style={{
                      fontSize: '0.7rem',
                      background: typeBgSubtle,
                      // text-emphasis is the contrast-safe partner for a *-bg-subtle chip.
                      color: typeTextEmphasis,
                      border: `1px solid ${typeBorderSubtle}`,
                    }}
                  >
                    {isExpense ? 'Expense' : 'Income'}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2 mt-1">
                  <span className={`badge ${getStatusBadgeClass(repeatingInvoice.status)}`} style={{ fontSize: '0.75rem' }}>
                    {getStatusLabel(repeatingInvoice.status)}
                  </span>
                  <span style={{ fontSize: '0.875rem', color: 'var(--bs-secondary-color)' }}>
                    {getScheduleDescription(repeatingInvoice.frequency)}
                  </span>
                  <span style={{ color: 'var(--bs-secondary-color)' }}>•</span>
                  <span className="fw-semibold" style={{ color: typeColor, fontSize: '0.875rem' }}>
                    {amountPrefix}{formatCurrency(total)}
                  </span>
                </div>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div
                  className="h-100 p-3 rounded-3 text-center"
                  style={{
                    background: 'rgba(var(--bs-primary-rgb, 59, 130, 246), 0.12)',
                    border: '1px solid rgba(var(--bs-primary-rgb, 59, 130, 246), 0.25)',
                  }}
                >
                  <i className="bi bi-calendar-check fs-3 d-block mb-2" style={{ color: 'var(--bs-primary)' }}></i>
                  <small className="d-block" style={{ color: 'var(--bs-secondary-color)' }}>Next scheduled</small>
                  <span className="fw-bold" style={{ color: 'var(--bs-primary)' }}>
                    {formatDate(repeatingInvoice.nextDate)}
                  </span>
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className="h-100 p-3 rounded-3 text-center"
                  style={{
                    background: 'var(--bs-tertiary-bg)',
                    border: '1px solid var(--bs-border-color)',
                  }}
                >
                  <i className="bi bi-arrow-repeat fs-3 d-block mb-2" style={{ color: 'var(--bs-primary)' }}></i>
                  <small className="d-block" style={{ color: 'var(--bs-secondary-color)' }}>Frequency</small>
                  <span className="fw-bold" style={{ color: 'var(--bs-body-color)' }}>
                    {getScheduleDescription(repeatingInvoice.frequency)}
                  </span>
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className="h-100 p-3 rounded-3 text-center"
                  style={{
                    background: 'var(--bs-tertiary-bg)',
                    border: '1px solid var(--bs-border-color)',
                  }}
                >
                  <i className="bi bi-currency-dollar fs-3 d-block mb-2" style={{ color: 'var(--bs-primary)' }}></i>
                  <small className="d-block" style={{ color: 'var(--bs-secondary-color)' }}>Total</small>
                  <span className="fw-bold" style={{ color: typeColor }}>
                    {amountPrefix}{formatCurrency(total)}
                  </span>
                </div>
              </div>
            </div>

            <div className="d-flex gap-3 mb-4">
              {repeatingInvoice.startDate && (
                <div
                  className="flex-fill p-3 rounded-3"
                  style={{
                    background: 'var(--bs-tertiary-bg)',
                    border: '1px solid var(--bs-border-color)',
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <i className="bi bi-calendar-event" style={{ color: 'var(--bs-secondary-color)' }}></i>
                    <small style={{ color: 'var(--bs-secondary-color)' }}>Start Date</small>
                  </div>
                  <span className="fw-semibold" style={{ color: 'var(--bs-body-color)' }}>{formatDate(repeatingInvoice.startDate)}</span>
                </div>
              )}
              {repeatingInvoice.endDate && (
                <div
                  className="flex-fill p-3 rounded-3"
                  style={{
                    background: 'var(--bs-tertiary-bg)',
                    border: '1px solid var(--bs-border-color)',
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <i className="bi bi-calendar-x" style={{ color: 'var(--bs-secondary-color)' }}></i>
                    <small style={{ color: 'var(--bs-secondary-color)' }}>End Date</small>
                  </div>
                  <span className="fw-semibold" style={{ color: 'var(--bs-body-color)' }}>{formatDate(repeatingInvoice.endDate)}</span>
                </div>
              )}
            </div>

            {repeatingInvoice.lineItems && repeatingInvoice.lineItems.length > 0 && (
              <div>
                <h6 className="mb-3 d-flex align-items-center gap-2" style={{ color: 'var(--bs-body-color)' }}>
                  <i className="bi bi-list-ul" style={{ color: 'var(--bs-secondary-color)' }}></i>
                  Line Items
                </h6>
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>
                        <th className="border-0 fw-semibold">Description</th>
                        <th className="border-0 fw-semibold text-center" style={{ width: '80px' }}>Qty</th>
                        <th className="border-0 fw-semibold text-end" style={{ width: '100px' }}>Unit Price</th>
                        <th className="border-0 fw-semibold text-end" style={{ width: '100px' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repeatingInvoice.lineItems.map((item, index) => (
                        <tr key={index} style={{ color: 'var(--bs-body-color)' }}>
                          <td className="border-0">{item.description || '-'}</td>
                          <td className="border-0 text-center">{item.quantity}</td>
                          <td className="border-0 text-end">{formatCurrency(item.unitAmount)}</td>
                          <td className="border-0 text-end fw-semibold">{formatCurrency(item.lineAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ color: 'var(--bs-body-color)' }}>
                        <td colSpan={3} className="text-end fw-bold border-0 pt-3">Total:</td>
                        <td className="text-end fw-bold border-0 pt-3" style={{ color: typeColor }}>
                          {amountPrefix}{formatCurrency(total)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer border-0 pt-0">
            <button
              type="button"
              className="btn"
              style={{
                background: 'var(--bs-secondary-bg)',
                border: '1px solid var(--bs-border-color)',
                color: 'var(--bs-body-color)',
              }}
              onClick={onClose}
            >
              Close
            </button>
            {/* Router navigation, not an <a href> — the anchor full-page-reloaded the SPA.
                Recurring templates live on the accounting page's Recurring view. */}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                onClose();
                navigate('/accounting?view=recurring');
              }}
            >
              <i className="bi bi-arrow-right me-1"></i>
              View All Recurring
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepeatingInvoiceModal;
